'use client';

import {
	Accordion, Center, For, RadioCard, SimpleGrid, Spinner,
} from '@chakra-ui/react';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { ResidenceStep } from '@/modules/authentication/view-model';
import { useProvinceListQuery } from '@/modules/shared';

import { LocationRegionSchema  } from '@packages/contracts';
import type { LocationRegion } from '@packages/contracts';

type DistrictOptionsProps = {
	region: LocationRegion
};

export const DistrictOptions = ({ region }: DistrictOptionsProps) => {
	const locale = useLocale();
	const { data: provinces, isLoading } = useProvinceListQuery(region);

	if (isLoading) return (
		<Center>
			<Spinner />
		</Center>
	);

	return (
		<SimpleGrid columns={2} gap="md">
			<For each={provinces} fallback={isLoading ? <Spinner /> : null}>
				{province => (
					<RadioCard.Item key={province.id} value={province.id}>
						<RadioCard.ItemHiddenInput />
						<RadioCard.ItemControl>
							<RadioCard.ItemText>
								{province.localized[locale]}
							</RadioCard.ItemText>
						</RadioCard.ItemControl>
					</RadioCard.Item>
				)}
			</For>
		</SimpleGrid>
	);
};

export const Residence = () => {
	const t = useTranslations('authentication.registration.form');
	const { control } = useFormContext<ResidenceStep>();

	return (
		<Controller
			control={control}
			name="provinceId"
			render={({ field }) => (
				<Accordion.Root
					collapsible
					lazyMount
					variant="outline"
				>
					<RadioCard.Root onValueChange={({ value }) => value && field.onChange(value)} value={field.value}>
						<For each={LocationRegionSchema.options}>
							{region => (
								<Accordion.Item key={region} value={region}>
									<Accordion.ItemTrigger justifyContent="space-between" p="xs" py="sm">
										{t('residence.region', { region })}
										<Accordion.ItemIndicator />
									</Accordion.ItemTrigger>
									<Accordion.ItemContent p="sm">
										<DistrictOptions region={region} />
									</Accordion.ItemContent>
								</Accordion.Item>
							)}
						</For>
					</RadioCard.Root>
				</Accordion.Root>
			)}
		/>
	);
};
